import React, { useState, useEffect } from 'react';

const DEFAULTS = {
  textSize: 'medium',
  highContrast: false,
  colorblindMode: false,
  slowPlay: false
};

function getPrefs() {
  try {
    const stored = localStorage.getItem('buck_euchre_a11y');
    return stored ? { ...DEFAULTS, ...JSON.parse(stored) } : DEFAULTS;
  } catch {
    return DEFAULTS;
  }
}

function savePrefs(prefs) {
  localStorage.setItem('buck_euchre_a11y', JSON.stringify(prefs));
  applyPrefs(prefs);
}

function applyPrefs(prefs) {
  const root = document.documentElement;
  const sizes = { small: '14px', medium: '16px', large: '20px' };
  root.style.fontSize = sizes[prefs.textSize] || '16px';

  if (prefs.highContrast) {
    root.classList.add('high-contrast');
  } else {
    root.classList.remove('high-contrast');
  }

  if (prefs.colorblindMode) {
    root.classList.add('colorblind-mode');
  } else {
    root.classList.remove('colorblind-mode');
  }
}

// Apply on load
applyPrefs(getPrefs());

export function useAccessibility() {
  const [prefs, setPrefs] = useState(getPrefs);

  const updatePref = (key, value) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    savePrefs(next);
  };

  return { prefs, updatePref };
}

export default function AccessibilityPanel({ onClose }) {
  const { prefs, updatePref } = useAccessibility();

  useEffect(() => {
    applyPrefs(prefs);
  }, [prefs]);

  const toggleStyle = (active) => ({
    background: active ? '#238636' : '#21262d',
    color: active ? '#fff' : '#8b949e',
    border: 'none', borderRadius: 6,
    padding: '6px 14px', fontSize: 12, fontWeight: 600,
    cursor: 'pointer', minWidth: 50
  });

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(0,0,0,0.6)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#161b22', border: '1px solid #30363d', borderRadius: 12,
          padding: 24, width: 340, maxWidth: '90vw',
          fontFamily: "'Georgia', serif", color: '#e2e8f0'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Accessibility</h2>
          <button onClick={onClose} style={{
            background: 'transparent', border: 'none', color: '#8b949e',
            fontSize: 18, cursor: 'pointer'
          }}>&times;</button>
        </div>

        {/* Text Size */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 12, color: '#8b949e', marginBottom: 6 }}>Text Size</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {['small', 'medium', 'large'].map(size => (
              <button
                key={size}
                onClick={() => updatePref('textSize', size)}
                style={toggleStyle(prefs.textSize === size)}
              >
                {size.charAt(0).toUpperCase() + size.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* High Contrast */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>High Contrast</div>
            <div style={{ fontSize: 11, color: '#8b949e' }}>Brighter colors, stronger borders</div>
          </div>
          <button
            onClick={() => updatePref('highContrast', !prefs.highContrast)}
            style={toggleStyle(prefs.highContrast)}
          >
            {prefs.highContrast ? 'On' : 'Off'}
          </button>
        </div>

        {/* Colorblind Mode */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Colorblind Mode</div>
            <div style={{ fontSize: 11, color: '#8b949e' }}>Shape indicators on card suits</div>
          </div>
          <button
            onClick={() => updatePref('colorblindMode', !prefs.colorblindMode)}
            style={toggleStyle(prefs.colorblindMode)}
          >
            {prefs.colorblindMode ? 'On' : 'Off'}
          </button>
        </div>

        {/* Slow Play */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600 }}>Slow Play</div>
            <div style={{ fontSize: 11, color: '#8b949e' }}>Double all turn timers</div>
          </div>
          <button
            onClick={() => updatePref('slowPlay', !prefs.slowPlay)}
            style={toggleStyle(prefs.slowPlay)}
          >
            {prefs.slowPlay ? 'On' : 'Off'}
          </button>
        </div>
      </div>
    </div>
  );
}
